import { SessionStorageKeys, useSessionStorage } from '@/utils/storage.ts';
import { Language } from '@/api/types.ts';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { detectBrowserLanguage } from '@/lib/language.ts';

export const DEFAULT_LANGUAGE = 'english';

export const languageToLocale: Record<Language, string> = {
  english: 'en',
  spanish: 'es',
  russian: 'ru',
  ukrainian: 'uk',
};

// Language state backed by a single session-storage key, kept in sync with
// i18next. Two keys exist on purpose — see the two hooks below.
const useLanguageState = (
  key: SessionStorageKeys,
  defaultLanguage: Language = DEFAULT_LANGUAGE
) => {
  const [language, _setLanguage] = useSessionStorage<Language>(
    key,
    defaultLanguage
  );
  const {
    i18n: { changeLanguage },
  } = useTranslation();

  useEffect(() => {
    changeLanguage(languageToLocale[language] ?? language);
  }, [language, changeLanguage]);

  const setLanguage = useCallback(
    (value: Language) => {
      _setLanguage(value);
      changeLanguage(languageToLocale[value] ?? value);
    },
    [_setLanguage, changeLanguage]
  );

  return [language, setLanguage, _setLanguage] as const;
};

// The apply-flow UI language. The ApplyFlowShell globe control writes this; the
// stored selection drives server content (?language=) and must never reach the
// interview — the interview language is chosen on the Welcome screen instead.
// On a first visit (nothing stored) the interface defaults to the visitor's
// browser language; an explicit pick via the globe switcher then takes over.
export const useAppLanguage = () =>
  useLanguageState(
    SessionStorageKeys.SELECTED_LANGUAGE,
    detectBrowserLanguage()
  );

// The language the interview is conducted in: the intro video and the questions.
// Written only by the Welcome screen, read only by useQuestionsAnswers.
export const useInterviewLanguage = () =>
  useLanguageState(SessionStorageKeys.INTERVIEW_LANGUAGE);
